import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, rupees } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Upload, FileCheck2, X, ShieldAlert, Tag, Check } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { user } = useAuth();
  const { items, subtotal, refresh, refreshLoyalty, loyalty, monthlySpend } = useCart() || { items: [], subtotal: 0, refresh: () => {}, refreshLoyalty: () => {}, loyalty: { amount: 0, tier: "none", percent: 0 }, monthlySpend: 0 };
  const [address, setAddress] = useState({
    label: "Home", line1: "", line2: "", city: "", state: "Rajasthan", pincode: "", phone: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [submitting, setSubmitting] = useState(false);
  const [razorpayEnabled, setRazorpayEnabled] = useState(false);
  const [rxFile, setRxFile] = useState(null);
  const [rxPath, setRxPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);   // { code, discount }
  const [couponBusy, setCouponBusy] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const deliveryFee = subtotal >= 500 || subtotal === 0 ? 0 : 40;
  const loyaltyDiscount = loyalty.amount;
  const discount = coupon?.discount || 0;
  const total = Math.max(subtotal + deliveryFee - discount - loyaltyDiscount, 0);
  const rxItems = items.filter((i) => i.medicine.prescription_required);
  const rxRequired = rxItems.length > 0;

  useEffect(() => {
    api.get("/config").then(({ data }) => setRazorpayEnabled(!!data.razorpay_enabled)).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim() });
      setCoupon({ code: data.code, discount: data.discount });
      toast.success(`Coupon ${data.code} applied · saved ${rupees(data.discount)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally {
      setCouponBusy(false);
    }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); };

  const validateAddress = () => {
    if (!address.line1 || !address.city || !address.pincode || !address.phone) {
      toast.error("Please complete your delivery address"); return false;
    }
    if (!/^\d{6}$/.test(address.pincode)) { toast.error("Pincode must be 6 digits"); return false; }
    if (!/^\d{10}$/.test(address.phone)) { toast.error("Phone must be 10 digits"); return false; }
    if (rxRequired && !rxPath) { toast.error("Please upload your prescription"); return false; }
    return true;
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("File must be under 8MB"); return; }
    setRxFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/prescriptions/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRxPath(data.path);
      toast.success("Prescription uploaded · AI is scanning it");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setRxFile(null);
    } finally {
      setUploading(false);
    }
  };
  const removeFile = () => {
    setRxFile(null); setRxPath(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const placeCod = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        address, payment_method: "cod",
        prescription_path: rxRequired ? rxPath : null,
        coupon_code: coupon?.code || null,
      });
      await refresh();
      await refreshLoyalty();
      toast.success("Order placed successfully!");
      navigate(`/orders/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  const placeRazorpay = async () => {
    if (!window.Razorpay) { toast.error("Razorpay SDK failed to load. Please refresh."); return; }
    setSubmitting(true);
    try {
      const { data: rzp } = await api.post("/orders/razorpay/create", { address });
      const options = {
        key: rzp.key_id, amount: rzp.amount, currency: rzp.currency, order_id: rzp.razorpay_order_id,
        name: "Shriram Pharma", description: `Order · ${items.length} item(s)`,
        prefill: { name: rzp.customer.name, email: rzp.customer.email, contact: rzp.customer.contact },
        theme: { color: "#1A362B" },
        handler: async (resp) => {
          try {
            const { data: order } = await api.post("/orders/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              address,
              prescription_path: rxRequired ? rxPath : null,
              coupon_code: coupon?.code || null,
            });
            await refresh();
            await refreshLoyalty();
            toast.success("Payment successful!");
            navigate(`/orders/${order.id}`, { replace: true });
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally { setSubmitting(false); }
        },
        modal: { ondismiss: () => { setSubmitting(false); toast.info("Payment cancelled"); } },
      };
      const rzInstance = new window.Razorpay(options);
      rzInstance.on("payment.failed", (resp) => {
        setSubmitting(false);
        toast.error(resp?.error?.description || "Payment failed");
      });
      rzInstance.open();
    } catch (e) {
      setSubmitting(false);
      toast.error(e?.response?.data?.detail || "Could not start payment");
    }
  };

  const placeOrder = () => {
    if (!validateAddress()) return;
    if (paymentMethod === "razorpay") placeRazorpay(); else placeCod();
  };

  if (!user) { navigate("/login"); return null; }

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="label-caps text-stone-500">Checkout</div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1A362B] tracking-tight mt-2 mb-10">Almost there</h1>

        {items.length === 0 ? (
          <p className="text-stone-500">Your cart is empty.</p>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-10">
              <section className="p-6 border border-stone-200 rounded-md bg-white">
                <div className="label-caps text-stone-500 text-xs mb-4">Delivery Address</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-xs label-caps text-stone-500">Address line 1</Label>
                    <Input value={address.line1} onChange={onChange("line1")} placeholder="House / Flat / Street" className="mt-1" data-testid="addr-line1"/>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs label-caps text-stone-500">Address line 2 (optional)</Label>
                    <Input value={address.line2} onChange={onChange("line2")} placeholder="Landmark / Area" className="mt-1" data-testid="addr-line2"/>
                  </div>
                  <div><Label className="text-xs label-caps text-stone-500">City</Label><Input value={address.city} onChange={onChange("city")} className="mt-1" data-testid="addr-city"/></div>
                  <div><Label className="text-xs label-caps text-stone-500">State</Label><Input value={address.state} onChange={onChange("state")} className="mt-1" data-testid="addr-state"/></div>
                  <div><Label className="text-xs label-caps text-stone-500">Pincode</Label><Input value={address.pincode} onChange={onChange("pincode")} maxLength={6} className="mt-1" data-testid="addr-pincode"/></div>
                  <div><Label className="text-xs label-caps text-stone-500">Phone</Label><Input value={address.phone} onChange={onChange("phone")} maxLength={10} className="mt-1" data-testid="addr-phone"/></div>
                </div>
              </section>

              {rxRequired && (
                <section className="p-6 border border-[#1A362B] rounded-md bg-white" data-testid="rx-section">
                  <div className="flex items-start gap-3 mb-4">
                    <ShieldAlert className="w-5 h-5 text-[#1A362B] mt-0.5 shrink-0"/>
                    <div>
                      <div className="label-caps text-stone-500 text-xs">Prescription</div>
                      <div className="text-sm text-[#1A362B] mt-1">
                        Upload a photo or PDF of your doctor&apos;s prescription for
                        <span className="font-semibold"> {rxItems.map((r) => r.medicine.name).join(", ")}</span>.
                        Our AI will pre-scan it, and our pharmacist will verify before dispatch.
                      </div>
                    </div>
                  </div>
                  {rxPath ? (
                    <div className="flex items-center gap-3 p-3 rounded-md bg-[#A3B19B]/20 border border-[#A3B19B]/60" data-testid="rx-uploaded">
                      <FileCheck2 className="w-5 h-5 text-[#1A362B]"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#1A362B] truncate">{rxFile?.name || "Prescription uploaded"}</div>
                        <div className="text-xs text-stone-600">AI scanning · pharmacist review after checkout</div>
                      </div>
                      <button onClick={removeFile} className="text-xs text-[#C44949] hover:underline" data-testid="rx-remove"><X className="w-4 h-4"/></button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-stone-300 rounded-md p-8 hover:border-[#1A362B] hover:bg-[#1A362B]/5 transition-colors cursor-pointer text-center" data-testid="rx-upload-box">
                      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" onChange={handleFile} className="hidden" data-testid="rx-upload-input"/>
                      <Upload className="w-6 h-6 text-stone-500"/>
                      <div className="mt-3 text-sm text-[#1A362B] font-medium">{uploading ? "Uploading…" : "Click to upload prescription"}</div>
                      <div className="mt-1 text-xs text-stone-500">JPG · PNG · PDF · up to 8 MB</div>
                    </label>
                  )}
                </section>
              )}

              <section className="p-6 border border-stone-200 rounded-md bg-white">
                <div className="label-caps text-stone-500 text-xs mb-4">Payment Method</div>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  <label className={`flex items-center gap-4 p-4 border rounded-md cursor-pointer transition-colors ${paymentMethod === "cod" ? "border-[#1A362B] bg-[#1A362B]/5" : "border-stone-200 hover:border-[#1A362B]"}`} data-testid="pay-cod">
                    <RadioGroupItem value="cod" id="cod"/>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1A362B]">Cash on Delivery</div>
                      <div className="text-xs text-stone-500">Pay in cash when the medicines reach your door</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-4 p-4 border rounded-md transition-colors ${!razorpayEnabled ? "opacity-60 cursor-not-allowed border-stone-200" : paymentMethod === "razorpay" ? "border-[#1A362B] bg-[#1A362B]/5 cursor-pointer" : "border-stone-200 hover:border-[#1A362B] cursor-pointer"}`} data-testid="pay-razorpay">
                    <RadioGroupItem value="razorpay" id="razorpay" disabled={!razorpayEnabled}/>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1A362B]">Razorpay · UPI · Cards · Netbanking</div>
                      <div className="text-xs text-stone-500">{razorpayEnabled ? "Secure online payment powered by Razorpay" : "Coming soon"}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-[10px] label-caps text-stone-400">UPI · VISA · MC · NB</div>
                  </label>
                </RadioGroup>
              </section>
            </div>

            <div className="lg:col-span-1">
              <div className="p-6 border border-stone-200 rounded-md bg-white sticky top-24">
                <div className="label-caps text-stone-500 text-xs">Order summary</div>
                <div className="mt-4 space-y-2 max-h-52 overflow-auto thin-scroll pr-1">
                  {items.map((it) => (
                    <div key={it.medicine.id} className="flex justify-between text-sm">
                      <span className="text-stone-700 truncate mr-2">{it.medicine.name} × {it.quantity}</span>
                      <span className="font-mono-num text-[#1A362B]">{rupees(it.medicine.price * it.quantity)}</span>
                    </div>
                  ))}
                </div>

                <Separator className="my-4"/>
                {/* Coupon */}
                {coupon ? (
                  <div className="flex items-center gap-2 p-3 bg-[#A3B19B]/15 border border-[#A3B19B]/50 rounded-md" data-testid="coupon-applied">
                    <Check className="w-4 h-4 text-[#1A362B]"/>
                    <div className="flex-1 text-xs">
                      <div className="text-[#1A362B] font-semibold">{coupon.code} applied</div>
                      <div className="text-stone-600">− {rupees(coupon.discount)}</div>
                    </div>
                    <button onClick={removeCoupon} className="text-[10px] text-[#C44949] hover:underline label-caps" data-testid="coupon-remove">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2" data-testid="coupon-row">
                    <div className="relative flex-1">
                      <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                      <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Coupon code" className="pl-9 h-10" data-testid="coupon-input"/>
                    </div>
                    <Button onClick={applyCoupon} disabled={couponBusy || !couponInput} variant="outline" className="h-10 rounded-md" data-testid="coupon-apply">
                      {couponBusy ? "…" : "Apply"}
                    </Button>
                  </div>
                )}

                <Separator className="my-4"/>
                <div className="space-y-2 font-mono-num text-sm">
                  <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span>{rupees(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">Delivery</span><span>{deliveryFee === 0 ? "Free" : rupees(deliveryFee)}</span></div>
                  {loyaltyDiscount > 0 && (
                    <div className="flex justify-between text-[#1A362B]" data-testid="checkout-loyalty-line">
                      <span>{loyalty.tier === "elite" ? "Elite · 20% off" : loyalty.tier === "gold" ? "Gold · 15% off (≥ ₹1,100)" : "Silver · 10% off (≥ ₹250)"}</span>
                      <span>− {rupees(loyaltyDiscount)}</span>
                    </div>
                  )}
                  {loyalty.tier !== "elite" && subtotal > 0 && subtotal < 250 && (
                    <p className="text-xs text-[#D96C4A]" data-testid="checkout-loyalty-nudge">Add {rupees(250 - subtotal)} more to unlock 10% off</p>
                  )}
                  {loyalty.tier === "silver" && subtotal > 0 && subtotal < 1100 && (
                    <p className="text-xs text-[#D96C4A]" data-testid="checkout-loyalty-nudge-gold">Add {rupees(1100 - subtotal)} more to upgrade to 15% off</p>
                  )}
                  {loyalty.tier === "elite" && (
                    <p className="text-xs text-[#1A362B] font-serif-display italic">Elite shopper · 30-day spend ₹{monthlySpend.toFixed(0)}</p>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between text-[#1A362B]"><span>Coupon ({coupon.code})</span><span>− {rupees(discount)}</span></div>
                  )}
                </div>
                <Separator className="my-4"/>
                <div className="flex justify-between font-serif-display text-xl text-[#1A362B]">
                  <span>Total</span>
                  <span className="font-mono-num">{rupees(total)}</span>
                </div>
                <Button
                  onClick={placeOrder}
                  disabled={submitting || uploading}
                  className="w-full mt-6 h-12 rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white active:scale-95 transition-transform"
                  data-testid="place-order-btn"
                >
                  {submitting ? "Processing…" : paymentMethod === "razorpay" ? `Pay online · ${rupees(total)}` : `Place order · ${rupees(total)}`}
                </Button>
                {paymentMethod === "razorpay" && (
                  <div className="mt-3 text-[10px] text-center text-stone-400 label-caps">Powered by Razorpay · Secure 256-bit SSL</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
